import {useEffect, useRef} from 'react';
import {Navigate, useSearchParams} from 'react-router-dom';

import {AfriexFeatureCode} from '@afriex/core/domain/enums';
import {BusinessFeeNegotiationFilter} from '@afriex/core/domain/types';

import {
  Button,
  Header,
  Pagination,
  SearchBar,
  TableSkeleton,
  Tabs,
} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import type {TabItem} from '@/components/Tabs/types';
import {appPaths} from '@/config/appPaths';
import {useFeatureActive} from '@/hooks/useFeatureActive';

import {NEGOTIATION_FILTER_LABELS, NEGOTIATION_TABS} from './negotiationFormat';
import NegotiationsTable from './NegotiationsTable';
import {NEGOTIATION_PAGE_SIZE, useFeeNegotiations} from './useFeeNegotiations';

const STATUS_PARAM = 'negotiationTab';
const MERCHANT_PARAM = 'merchantId';
const SELECTED_PARAM = 'negotiationId';

const EMPTY_TEXT: Partial<Record<BusinessFeeNegotiationFilter, string>> = {
  [BusinessFeeNegotiationFilter.AwaitingAdmin]: 'Nothing is waiting on AFX.',
  [BusinessFeeNegotiationFilter.AwaitingBusiness]:
    'Nothing is waiting on a merchant.',
  [BusinessFeeNegotiationFilter.Completed]:
    'No completed pricing requests yet.',
};
const NO_MATCH_TEXT = 'No pricing request matches this search.';

const parseStatus = (value: string | null): BusinessFeeNegotiationFilter =>
  NEGOTIATION_TABS.find((tab) => tab === value) ??
  BusinessFeeNegotiationFilter.AwaitingAdmin;

/**
 * Pricing requests merchants send and Afriex answers, as a queue. The tab, the
 * merchant search and the open request live in the URL, so a view can be
 * linked to from an email or from the merchant's own page.
 */
const PricingNegotiationsScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const status = parseStatus(searchParams.get(STATUS_PARAM));
  const merchantSearch = searchParams.get(MERCHANT_PARAM) ?? '';
  const expandedId = searchParams.get(SELECTED_PARAM);

  const {
    negotiations,
    total,
    counts,
    page,
    setPage,
    isLoading,
    error,
    refresh,
  } = useFeeNegotiations({status, merchantId: merchantSearch});

  /** Merges into the current URL so no other parameter is dropped. */
  const updateParams = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(window.location.search);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    });
    setSearchParams(next, {replace: true});
  };

  const handleMerchantSearch = (value: string) => {
    const trimmed = value.trim();
    // The search box reports its own mount; nothing to do if nothing changed.
    if (trimmed === merchantSearch) {
      return;
    }
    updateParams({[MERCHANT_PARAM]: trimmed || null, [SELECTED_PARAM]: null});
  };

  const handleTabChange = (tab: TabItem) =>
    updateParams({[STATUS_PARAM]: tab.id, [SELECTED_PARAM]: null});

  // One request open at a time; opening the open one closes it.
  const toggle = (id: string) =>
    updateParams({[SELECTED_PARAM]: id === expandedId ? null : id});

  // A link that names only the merchant opens its request when that leaves
  // one. Once, and never when the link already named a request.
  const wantsDeepLinkOpen = useRef(Boolean(merchantSearch) && !expandedId);
  useEffect(() => {
    if (isLoading || !wantsDeepLinkOpen.current) {
      return;
    }
    wantsDeepLinkOpen.current = false;
    if (negotiations.length === 1 && negotiations[0].id) {
      updateParams({[SELECTED_PARAM]: negotiations[0].id});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once, when the first page of results lands
  }, [isLoading]);

  const openCount =
    counts[BusinessFeeNegotiationFilter.AwaitingAdmin] +
    counts[BusinessFeeNegotiationFilter.AwaitingBusiness];
  const countSuffix = openCount > 0 ? `(${openCount.toLocaleString()})` : '';

  const tabs: TabItem[] = NEGOTIATION_TABS.map((filter) => ({
    id: filter,
    title:
      filter === BusinessFeeNegotiationFilter.Completed
        ? NEGOTIATION_FILTER_LABELS[filter]
        : `${NEGOTIATION_FILTER_LABELS[filter]} (${counts[filter]})`,
    content: null,
  }));

  const handlePageChange: PaginationProps['onPageChange'] = ({
    page: pageIndex = 0,
  }) => {
    setPage(pageIndex + 1);
    return Promise.resolve();
  };

  const renderQueue = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }
    if (error) {
      return (
        <div className="py-8 text-center">
          <p className="mb-3 text-sm text-red-600">{error}</p>
          <Button variant="outline" colorScheme="cyan" onClick={refresh}>
            Try again
          </Button>
        </div>
      );
    }
    if (!negotiations.length) {
      return (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          {merchantSearch ? NO_MATCH_TEXT : EMPTY_TEXT[status]}
        </p>
      );
    }
    return (
      <>
        <NegotiationsTable
          negotiations={negotiations}
          isCompleted={status === BusinessFeeNegotiationFilter.Completed}
          expandedId={expandedId}
          onToggle={toggle}
          onAnswered={() => {
            updateParams({[SELECTED_PARAM]: null});
            refresh();
          }}
        />
        <Pagination
          limit={NEGOTIATION_PAGE_SIZE}
          total={total}
          currentPageOverride={page}
          onPageChange={handlePageChange}
        />
      </>
    );
  };

  return (
    <main className="my-5 mx-4 md:mx-10">
      <Header title={`Pricing Negotiations ${countSuffix}`.trim()} />

      <div className="my-4 max-w-md">
        <SearchBar
          placeholder="Search by merchant name or ID"
          initialSearchTerm={merchantSearch}
          handleSearch={handleMerchantSearch}
          handleAutoComplete={handleMerchantSearch}
          clearFilters={() => handleMerchantSearch('')}
        />
      </div>

      <Tabs
        tabs={tabs}
        queryKey={STATUS_PARAM}
        activeTabId={status}
        onTabChange={handleTabChange}
      />

      {renderQueue()}
    </main>
  );
};

/**
 * The queue exists only while pricing requests are on for some business. When
 * the check itself fails the page still opens: the server gates the data.
 */
const PricingNegotiationsRoute = () => {
  const {isActive, hasLoaded, hasFailed} = useFeatureActive(
    AfriexFeatureCode.BUSINESS_PRICING_NEGOTIATION,
  );
  if (!hasLoaded) {
    return null;
  }
  return isActive || hasFailed ? (
    <PricingNegotiationsScreen />
  ) : (
    <Navigate to={`/${appPaths.merchants}`} replace />
  );
};

export default PricingNegotiationsRoute;
